import _ from "lodash";
import { ControlType } from "marketplace/_views/filters/filter-list";

export type FilterChipValueType = string | string[] | boolean | number | number[] | null;

export interface FilterChip {
  readonly filterName: string;
  readonly enabled: boolean;
  readonly default: FilterChipValueType;
  readonly controlType: ControlType;
  value: FilterChipValueType;
  title: string;
  label: string;
  hidden: boolean;
  freeSolo?: boolean;
  multiple?: boolean;
  options?: string[];
  min?: number;
  max?: number;
  steps?: number;
  precision?: number;
  enable(): void;
  disable(): void;
}

export class TextChip implements FilterChip {
  public readonly filterName: string;
  public readonly default: string;
  public readonly controlType: ControlType;
  public value: string;
  public title: string;
  public label: string;
  public enabled: boolean;
  public hidden: boolean = false;

  public constructor(filterName: string, title: string, enabled: boolean = false) {
    this.filterName = filterName;
    this.title = title;
    this.enabled = enabled;
    this.default = '';
    this.value = '';
    this.label = `${this.title} - Any`;
    this.controlType = ControlType.Text;
  }

  public updateValue(value: string): void {
    this.value = value;
    this.label = value.length > 0 ?
      `${this.title}: ${value}` :
      `${this.title} - Any`;

    this.enable();
  }

  public enable(): void {
    this.enabled = true;
  }

  public disable(): void {
    this.enabled = false;
    this.label = `${this.title} - Any`;
    this.value = '';
  }
}

export class RatingChip implements FilterChip {
  public readonly filterName: string;
  public readonly default: number | null;
  public readonly controlType: ControlType;
  public value: number | null;
  public title: string;
  public label: string;
  public enabled: boolean;
  public hidden: boolean = false;
  public precision?: number;

  public constructor(filterName: string, title: string, enabled: boolean = false) {
    this.filterName = filterName;
    this.title = title;
    this.enabled = enabled;
    this.default = 0;
    this.value = 0;
    this.label = `${this.title} - Any`;
    this.controlType = ControlType.Rating;
  }

  public updateValue(value: string): void {
    const newValue = Number(value);

    if (newValue < 0 || newValue > 5) {
      throw new RangeError('Rating value must be between 0 and 5');
    }

    this.value = newValue;

    this.label = newValue > 0 ?
      `${this.title}: ${value}` :
      `${this.title} - Any`;

    this.enable();
  }

  public enable(): void {
    this.enabled = true;
  }

  public disable(): void {
    this.enabled = false;
    this.label = `${this.title} - Any`;
    this.value = 0;
  }
}

export class ArrayChip implements FilterChip {
  public readonly filterName: string;
  public readonly default: string[];
  public readonly controlType: ControlType;
  public value: string[];
  public title: string;
  public label: string;
  public enabled: boolean;
  public hidden: boolean = false;
  public freeSolo?: boolean;
  public multiple?: boolean;
  public options?: string[];

  public constructor(filterName: string, title: string, enabled: boolean = false) {
    this.filterName = filterName;
    this.title = title;
    this.enabled = enabled;
    this.default = [];
    this.value = [];
    this.label = `${this.title} - Any`;
    this.controlType = ControlType.Autocomplete;
  }

  public updateValue(value: string[]) {
    this.value = value;

    this.label = _.isEmpty(value) ?
      `${this.title} - Any` :
      `${this.title}: ${value.join(', ')}`;

    this.enable();
  }

  public enable(): void {
    this.enabled = true;
  }

  public disable(): void {
    this.enabled = false;
    this.label = `${this.title} - Any`;
    this.value = [];
  }
}

export class RangeChip implements FilterChip {
  public readonly filterName: string;
  public readonly default: number | number[];
  public readonly controlType: ControlType;
  public value: number | number[];
  public title: string;
  public label: string;
  public enabled: boolean;
  public hidden: boolean = false;
  public min?: number;
  public max?: number;
  public steps?: number;

  public constructor(filterName: string, title: string, enabled: boolean = false) {
    this.filterName = filterName;
    this.title = title;
    this.enabled = enabled;
    this.default = [];
    this.value = [];
    this.label = `${this.title} - Any`;
    this.controlType = ControlType.Slider;
  }

  public updateValue(value: string[]) {
    this.value = value.map(Number);

    this.label = _.isEmpty(value) ?
      `${this.title} - Any` :
      `${this.title}: ${value.join(' - ')}`;

    this.enable();
  }

  public enable(): void {
    this.enabled = true;
  }

  public disable(): void {
    this.enabled = false;
    this.label = `${this.title} - Any`;
    this.value = [];
  }
}

export class NumericChip implements FilterChip {
  public readonly filterName: string;
  public readonly default: number;
  public readonly controlType: ControlType;
  public value: number | null;
  public title: string;
  public label: string;
  public enabled: boolean;
  public hidden: boolean = false;

  public constructor(filterName: string, title: string, enabled: boolean = false) {
    this.filterName = filterName;
    this.title = title;
    this.enabled = enabled;
    this.default = 0;
    this.value = 0;
    this.label = `${this.title} - Any`;
    this.controlType = ControlType.Numeric;
  }

  public updateValue(value: number | string | null) {
    this.label = `${this.title}: ${value}`
    if (typeof value === 'string') {
      this.value = Number.parseInt(value);
    }
    else {
      this.value = value;
    }
    this.enable();
  }

  public enable(): void {
    this.enabled = true;
  }

  
  public disable(): void {
    this.enabled = false;
    this.label = `${this.title} - Any`;
    this.value = null;
  }
}

export class BoolChip implements FilterChip {
  public readonly filterName: string;
  public readonly default: boolean;
  public readonly controlType: ControlType;
  public value: boolean;
  public title: string;
  public label: string;
  public enabled: boolean;
  public hidden: boolean = false;

  public constructor(filterName: string, title: string, enabled: boolean = false) {
    this.filterName = filterName;
    this.title = title;
    this.enabled = enabled;
    this.default = false;
    this.value = false;
    this.label = `${this.title} - Any`;
    this.controlType = ControlType.Switch;
  }

  public updateValue(value: string | boolean) {
    const newValue = typeof value === 'boolean' ? value : value === 'true';
    this.value = newValue;
    this.label = `${this.title}: ${newValue ? 'Yes' : 'No'}`;

    this.enable();
  }

  public enable(): void {
    this.enabled = true;
  }

  public disable(): void {
    this.enabled = false;
    this.label = `${this.title} - Any`;
    this.value = false;
  }
}
